import React, { useState, useEffect, useCallback, useMemo } from 'react';

import Keycloak from 'keycloak-js';
import { UserContext, useLanguage, useLocalStorage, useInterceptor, IRestApiUserAuthorities } from '@oceanjs/core';

import { WisaUserProviderProps } from './WisaUserProvider.types';
import { AppEnvironmentApi } from '../../api/environment/AppEnvironmentApi';
import { IGetUserResponse } from '../../api/environment';
import { WisaUser } from './WisaUser';
import { WisaSpinner } from '../Common/WisaSpinner';

enum StorageId {
  KeycloakToken = 'kc_token',
  KeycloakRefreshToken = 'kc_refreshToken',
}

class KeycloakError extends Error {
  inner: Keycloak.KeycloakError | undefined;

  constructor(message: string | undefined, inner?: Keycloak.KeycloakError) {
    super(message);
    this.inner = inner;
  }
}

/**
 * An UserProvider that deals with authentication
 * through KeyCloak.
 */
export const WisaUserProvider: React.FC<WisaUserProviderProps> = (props: Readonly<WisaUserProviderProps>): JSX.Element => {
  const { children, logoutRoute, responseMode, getKeycloakInitOptions } = props;
  const [user, setUser] = useState<WisaUser | null>(null);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [pvpAuthenticated, setPvpAuthenticated] = useState<boolean>(false);
  const [error, setError] = useState<KeycloakError | undefined>();
  const language = useLanguage();
  const [token, setToken] = useLocalStorage<string | undefined>(StorageId.KeycloakToken, undefined);
  const [refreshToken, setRefreshToken] = useLocalStorage<string | undefined>(StorageId.KeycloakRefreshToken, undefined);

  const [keycloak, setKeycloak] = useState<Keycloak | undefined>();

  /**
   * init keycloak instance
   */
  useEffect(() => {
    (async () => {
      const result = await getKeycloakInitOptions();

      if (!result) {
        setError(new KeycloakError('KeyCloak initialization failed'));
        return;
      }
      setKeycloak(new Keycloak({clientId: result.clientId, realm: result.realm, url: result.url}));
    })();

    return () => {
      setKeycloak(undefined);
    };
  }, [getKeycloakInitOptions]);

  /**
   * Saves the token that keycloak currently uses
   * to the local storage
   */
  const rememberToken = useCallback((): void => {
    if (keycloak === undefined) return;

    if (keycloak.token !== undefined) {
      setToken(keycloak.token);
    }
    if (keycloak.refreshToken !== undefined) {
      setRefreshToken(keycloak.refreshToken);
    }
  }, [setRefreshToken, setToken, keycloak]);

  /**
   * 
   */
  const getUserInfo = useCallback(async (): Promise<IGetUserResponse> => {
    const user = await AppEnvironmentApi.getUserInfo();
    return user?.data ?? 'anonymousUser';
  }, []);
  /**
   * Maps the keycloak user to an oceanjs compatible one
   */
  const generateUser = useCallback((): WisaUser | null => {
    if (keycloak === undefined || keycloak.tokenParsed === undefined) {
      return null;
    }
    const tokenParsed = keycloak.tokenParsed;
    const userRoles = tokenParsed.realm_access !== undefined ? tokenParsed.realm_access.roles : [];
    
    const username = (tokenParsed as any).preferred_username as string;
    const email = (tokenParsed as any).email as string;
    const authorities: IRestApiUserAuthorities[] = [];
    userRoles.forEach((r) => {
      if(r.startsWith('EHPC_')) {
        authorities.push({ authority: `ROLE_${r}`});
      }
    });

    return {
      roles: userRoles,
      username,
      email,
      hasRole: (role: string): boolean => userRoles.includes(role),
      hasAnyRole: (roles: string[]): boolean => roles.find(r => userRoles.includes(r)) !== undefined,
      hasAllRoles: (roles: string[]): boolean => roles.every(r => userRoles.includes(r)),
      authorities: authorities,
      gvGid: null,
    };
  }, [keycloak]);


  /**
   * create user from pvp infos 
   */
  const createPvpUser = (username: string, gvGid: string, email: string, authorities: IRestApiUserAuthorities[] ): WisaUser => {
    const roles = authorities.map(a => a.authority.replace('ROLE_',''));
    
    return {
      username,
      email,
      authorities,
      gvGid,
      roles: roles,
      hasRole: (role: string): boolean => roles.includes(role),
      hasAnyRole: (roles: string[]): boolean => roles.find(r => roles.includes(r)) !== undefined,
      hasAllRoles: (roles: string[]): boolean => roles.every(r => roles.includes(r)),
    }
  }

  /**
   * Adds the Bearer token from keycloak to each request
   */
  useInterceptor('request', config => {
    if (keycloak === undefined) return config;

    if (keycloak.tokenParsed) {
      return new Promise((resolve, reject): void => {
        keycloak
          .updateToken(5)
          .then((refreshed: boolean) => {
            if (refreshed) {
              rememberToken();
            }
            const authHeader = { Authorization: `Bearer ${keycloak.token}` };

            if(config.headers) {
              // @ts-ignore
              config.headers = {...config.headers, ...authHeader}
            } else {
              // @ts-ignore
              config.headers = {...config.headers, ...authHeader}
            }
       
            return resolve(config);
          })
          .catch(() => {
            keycloak.login();

            return reject();
          });
      });
    }

    return config;
  });

  /**
   * check pvp before keycloak init 
   * Initializes keycloak, which will check if the current token
   * is valid
   */
  useEffect(() => {
    // check user info to detect if user is already logged in with pvp 
    const userInfo = async () => {
      const result = await getUserInfo()
      // setUser for PVP
      if(result !== 'anonymousUser' && result.gvGid && result.gvGid !== null) {
        
        setUser(createPvpUser(result.username, result.gvGid as string, result.email, result.authorities));
        setPvpAuthenticated(true);
      }
    }
    userInfo();
    
    if (pvpAuthenticated) return;

    // cancel if keycloak is not cunstructed or already initialized
    if (initialized || keycloak === undefined) return;

    // init keycloak for keycloak login
    keycloak
      .init({ token, refreshToken, responseMode })
      .then((authenticated: boolean) => {
        setInitialized(true);
        // maybe not necessary
        if (!authenticated && !pvpAuthenticated) {
          setUser(null);
          return;
        }
        const user = generateUser();
        setUser(user);
        rememberToken();
      })
      .catch((error: Keycloak.KeycloakError) => {
        setUser(null);
        setInitialized(true);
        setError(new KeycloakError('KeyCloak initialization failed', error));
      });
  }, [generateUser, initialized, refreshToken, rememberToken, token, keycloak, responseMode, getUserInfo, pvpAuthenticated]);

  /**
   * Callback to force a login through keycloak - will redirect
   * the user to the keycloak login page
   */
  const login = useCallback(async () => {
    if (keycloak === undefined) return;

    keycloak.login({
      locale: language,
    });
  }, [language, keycloak]);

  /**
   * Callback to force a logout through keycloak
   */
  const logout = useCallback(async () => {
    if (keycloak === undefined) return;
    setUser(null);
    setToken(undefined);
    setRefreshToken(undefined);

    const homePath = `${window.location.href.replace(window.location.hash, '')}#${logoutRoute}`;

    keycloak.logout({
      redirectUri: homePath,
    });
  }, [setRefreshToken, setToken, keycloak, logoutRoute]);

  /**
   * Returns an url for accessing the account settings
   * page of the current user
   */
  const getAccountUrl = useCallback(() => {
    if (keycloak === undefined) return;

    return keycloak.createAccountUrl();
  }, [keycloak]);

   /**
   * returns refreshed authentication token
   */
  const getAuthenticationToken = useCallback(() => {
    if (keycloak === undefined) return undefined;
    let auth = '';
    if (keycloak.tokenParsed) {
      return new Promise((resolve, reject): any => {
        keycloak
          .updateToken(10)
          .then((refreshed: boolean) => {
            if (refreshed) {
              rememberToken();
            }

            auth = `Bearer ${keycloak.token}`;

            return resolve(auth);
          })
          .catch(() => {
            keycloak.login();

            return reject();
          });
      });
    }
  }, [keycloak, rememberToken]);



  /**
   * Contains the specialized keycloak userContext
   */
  const userContext = useMemo(
    () => {
      return ({
        initialized,
        user,
        login,
        logout,
        getAccountUrl,
        getAuthenticationToken,
      })
    },
    [getAccountUrl, getAuthenticationToken, initialized, login, logout, user],
  );


  if (!initialized) {
    return <WisaSpinner />;
  }

  return <UserContext.Provider value={userContext}>{children}</UserContext.Provider>;
};
